/**
 * Attendance Page — TimerWidget check-in/out + 30-day trend chart + attendance log list.
 *
 * Loads today's attendance status on mount, then allows check-in or check-out.
 * Shows a 30-day attendance trend (area chart) from the BLL.
 * The list below shows historical records via Refine ListPage.
 */

import * as React from 'react';
import { TimerWidget, ListPage, StatusBadge, ChartContainer, Card, CardContent, CardHeader, CardTitle } from '@yesbos/ui';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

// ── BLL helper ────────────────────────────────────────────────────────────────

async function callBLL(path: string, method = 'GET', body?: unknown) {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

// ── Types ────────────────────────────────────────────────────────────────────

interface TodayAttendance {
  id?: string;
  check_in?: string;
  check_out?: string;
  total_hours?: number;
  status?: 'present' | 'half_day' | 'on_leave' | 'absent';
}

interface TrendPoint {
  date: string;
  present: number;
  absent: number;
  on_leave: number;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function AttendanceListPage() {
  const [today, setToday] = React.useState<TodayAttendance | null>(null);
  const [loadingToday, setLoadingToday] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [toast, setToast] = React.useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [trend, setTrend] = React.useState<TrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = React.useState(true);

  // ── Load today's attendance ───────────────────────────────────────────────
  React.useEffect(() => {
    async function fetchToday() {
      setLoadingToday(true);
      try {
        const res = await callBLL('/v1/hros/attendance/today');
        setToday(res.success ? (res.data as TodayAttendance) : {});
      } catch {
        setToday({});
      } finally {
        setLoadingToday(false);
      }
    }
    fetchToday();
  }, []);

  // ── Load 30-day attendance trend ──────────────────────────────────────────
  React.useEffect(() => {
    callBLL('/v1/hros/dashboard/attendance-trend?days=30')
      .then((res) => { if (res.success) setTrend(res.data ?? []); })
      .catch(() => {})
      .finally(() => setTrendLoading(false));
  }, []);

  // ── Toast auto-dismiss ────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Check-in ──────────────────────────────────────────────────────────────
  async function handleCheckin() {
    setActionLoading(true);
    try {
      const res = await callBLL('/v1/hros/attendance/checkin', 'POST');
      if (res.success) {
        setToday(res.data as TodayAttendance);
        setToast({ message: 'Checked in successfully!', type: 'success' });
      } else {
        setToast({ message: res.error?.message ?? 'Check-in failed.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error. Please try again.', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  }

  // ── Check-out ─────────────────────────────────────────────────────────────
  async function handleCheckout() {
    setActionLoading(true);
    try {
      const res = await callBLL('/v1/hros/attendance/checkout', 'POST');
      if (res.success) {
        setToday(res.data as TodayAttendance);
        setToast({ message: 'Checked out successfully!', type: 'success' });
      } else {
        setToast({ message: res.error?.message ?? 'Check-out failed.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error. Please try again.', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  }

  const isCheckedIn = Boolean(today?.check_in && !today?.check_out);

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Attendance</h1>
        {today?.status && <StatusBadge status={today.status} />}
      </div>

      {/* Timer widget + today summary */}
      <div className="flex flex-col gap-3">
        <div className="max-w-sm">
          {loadingToday ? (
            <div className="h-28 rounded-lg border bg-muted animate-pulse" />
          ) : (
            <TimerWidget
              isCheckedIn={isCheckedIn}
              checkinTime={today?.check_in}
              onCheckin={actionLoading ? () => {} : handleCheckin}
              onCheckout={actionLoading ? () => {} : handleCheckout}
            />
          )}
        </div>

        {today?.check_in && (
          <div className="text-sm text-muted-foreground">
            Checked in at{' '}
            <span className="font-medium text-foreground">
              {new Date(today.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            {today.check_out && (
              <>
                {' '}· Checked out at{' '}
                <span className="font-medium text-foreground">
                  {new Date(today.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                {today.total_hours != null && (
                  <span className="ml-2 text-foreground font-medium">
                    ({today.total_hours.toFixed(1)}h worked)
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* 30-day attendance trend chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Attendance — Last 30 Days</CardTitle>
        </CardHeader>
        <CardContent>
          {trendLoading ? (
            <div className="h-48 rounded bg-muted animate-pulse" />
          ) : trend.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No attendance data yet</p>
          ) : (
            <ChartContainer
              type="area"
              data={trend}
              series={[
                { dataKey: 'present', name: 'Present', color: '#16a34a' },
                { dataKey: 'on_leave', name: 'On Leave', color: '#ca8a04' },
                { dataKey: 'absent', name: 'Absent', color: '#dc2626' },
              ]}
              xAxisKey="date"
              height={200}
              xAxisFormat="dayMonth"
            />
          )}
        </CardContent>
      </Card>

      {/* Historical log */}
      <div>
        <h2 className="text-lg font-medium mb-3">Attendance Records</h2>
        <ListPage
          collection="attendance_logs"
          title=""
          basePath="/attendance"
          excludeFields={['org_id', 'created_by', 'updated_at']}
          pageSize={25}
        />
      </div>
    </div>
  );
}

export default AttendanceListPage;
